import type { Direction, Hand, RhythmDensity, ScaleCell } from 'Types/ExerciseContentTypes';

/**
 * The read shape the skeleton pre-practice screen renders (MUS-312).
 *
 * Decision #131.1 seam: the screen is props-typed on this read model, and only
 * the container knows a fixture produces it. When the MUS-318 read endpoint
 * lands the resolved nested Exercise tree, the fixture is swapped for the API
 * and the screen does not change.
 *
 * This is deliberately the minimal consumer-local read model, per v4's
 * build-when-a-consumer-exists posture: `ExerciseContentTypes.ts` ships the
 * normalized rows (FK ids) and defers the full nested tree to MUS-318, so
 * MUS-312 (the first consumer) declares exactly the resolved shape it renders
 * and no more. Every field's vocabulary is drawn from the MUS-308 row types.
 */

/**
 * One tier waypoint as the pre-practice outline shows it: the schema 3.3 `wp_*`
 * columns resolved to display terms. `tempo` is a meets-or-beats threshold, not
 * an exact-match gate.
 */
export interface PrePracticeTier {
  tierNumber: number; // 1 / 2 / 3 = Beginner / Intermediate / Advanced
  hand: Hand;
  rangeOctaves: number;
  direction: Direction;
  rhythmDensity: RhythmDensity;
  tempo: number;
}

/** One level of the exercise ladder (schema 3.2), with its tiers nested. */
export interface PrePracticeLevel {
  levelNumber: number;
  title: string;
  isReview: boolean;
  description: string | null;
  tiers: PrePracticeTier[];
  /**
   * The cell coordinates this level scopes - the schema 3.2 `level_cell` rows,
   * one per (scale, tonic) the level teaches (e.g. Level 1 = C, G, D). Structure,
   * not grip: fixture-sourced today, supplied by MUS-318's read endpoint later,
   * exactly like the rest of the tree. A review level carries the union of the
   * prior content levels' cells (schema 3.2). This is the join the live-progress
   * overlay needs: a tier's grip is read from the frontiers of these cells
   * (see {@link tierProgressKey} and `deriveTierProgress`).
   */
  cells: ScaleCell[];
}

/** How the exercise is classified, resolved from the lookup rows to labels. */
export interface PrePracticeClassification {
  topicLabel: string; // topic.label, e.g. 'Scales & Keys'
  approachLabel: string; // approach.label, e.g. 'by playing'
  contentClassCode: string; // content_class.code, e.g. 'BL-020'
  sessionPatternCode: string; // session_pattern.code, e.g. 'SP-03'
}

/**
 * The grip state of one tier waypoint, in ascending order of progress:
 * untouched (never played) -> attempted (played, clearance bar not yet met) ->
 * cleared (met the clearance policy, cumulative-3 clean-or-better) -> mastered
 * (met the mastery policy, streak-3 flawless). #117: mastery =
 * flawless is the distinct, celebrated state (the star), never merged into
 * "cleared".
 */
export type TierState = 'untouched' | 'attempted' | 'cleared' | 'mastered';

/**
 * Per-tier grip, keyed by `${levelNumber}:${tierNumber}` (see
 * {@link tierProgressKey}). A missing key reads as 'untouched'.
 *
 * This is the grip half of territory (#124), which is persisted
 * separately from the exercise structure and is not authored content. In the
 * skeleton it is absent, so the screen renders the honest day-one empty state
 * (all tiers untouched); MUS-318's read endpoint supplies real grip with no
 * screen edit, exactly as the structure fixture is swapped (Decision #131.1).
 * Never fabricate grip in the shipped fixture.
 */
export type TierProgressMap = Record<string, TierState>;

/** The stable key for one tier's grip within {@link TierProgressMap}. */
export function tierProgressKey(levelNumber: number, tierNumber: number): string {
  return `${levelNumber}:${tierNumber}`;
}

/** The whole pre-practice read model for one exercise. */
export interface ExercisePrePracticeReadModel {
  /**
   * The exercise's numeric id (schema 3.1 `exercise.id`). The live-progress slice
   * (MUS-318) is keyed by this id, so the container needs it to hydrate and read
   * the per-cell frontier. Structure, fixture-sourced today.
   */
  exerciseId: number;
  slug: string;
  title: string;
  /**
   * A one-line orientation blurb, resolved from `content_class.description`.
   * Null until authored (it is null in the slice), so the screen renders no
   * subtitle rather than inventing copy.
   */
  description: string | null;
  classification: PrePracticeClassification;
  levels: PrePracticeLevel[];
  /**
   * Grip per tier. Optional and absent in the skeleton (honest empty state);
   * MUS-318 supplies it. Absent or missing entries read as 'untouched'.
   */
  progress?: TierProgressMap;
}
